import React, { useEffect, useState } from "react"
import axiosServices from "@pma/utils/axios"
import { useMutation } from "@tanstack/react-query"
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  X,
} from "lucide-react"

import { Button } from "@components/common/button"
import { Input } from "@components/common/input"
import { Checkbox } from "@components/common/checkbox"
import { Badge } from "@components/common/badge"
import { ScrollArea } from "@components/common/scroll-area"
import { Textarea } from "@components/common/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"

import type { DictType } from "@pma/types"
import { cn, getFinancialErrorMessage } from "@pma/utils/helpers"
import { formatCurrency } from "@pma/utils/utils"
import { BILL_APPROVE_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes"
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface BillComparisonViewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  bill: DictType
  revalidate: CallableFunction
}

interface ItemState {
  approved: number
  declined: boolean
  note: string
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function BillComparisonView({
  open,
  onOpenChange,
  bill,
  revalidate,
}: BillComparisonViewProps) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  // Item-level state: approved amounts, declined flags, notes
  const [itemStates, setItemStates] = useState<Record<string, ItemState>>({})
  // Which rows have expanded notes
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())
  // Selection for bulk actions
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())

  const billItems: DictType[] = bill?._bill_items?.filter((i: DictType) => i?.amount) || []

  // Initialize state from bill items
  useEffect(() => {
    const initial: Record<string, ItemState> = {}
    billItems.forEach((item: DictType) => {
      initial[item.id] = {
        approved: item.approved_amount ?? item.amount ?? 0,
        declined: false,
        note: "",
      }
    })
    setItemStates(initial)
    setSelectedItems(new Set(billItems.map((i: DictType) => String(i.id))))
  }, [bill?.id, bill?._bill_items?.length])

  // ── Derived data per item ──
  const getItemMetrics = (item: DictType) => {
    const contractQty = Number(item?._boq_item?.amount) || 0
    const alreadyPaid = Number(item?._boq_item?.paid) || 0
    const requested = Number(item?.amount) || 0
    const approved = itemStates[item.id]?.approved ?? requested
    const remainingAfter = contractQty - alreadyPaid - approved
    const isOverRequest = requested > contractQty - alreadyPaid
    const isApprovedOver = approved > contractQty - alreadyPaid

    return { contractQty, alreadyPaid, requested, approved, remainingAfter, isOverRequest, isApprovedOver }
  }

  // ── State updaters ──
  const updateApproved = (itemId: string, value: number) => {
    setItemStates((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], approved: value },
    }))
  }

  const updateDeclined = (itemId: string, declined: boolean) => {
    setItemStates((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], declined },
    }))
  }

  const updateNote = (itemId: string, note: string) => {
    setItemStates((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], note },
    }))
  }

  const toggleNoteExpand = (itemId: string) => {
    setExpandedNotes((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  const toggleSelectItem = (itemId: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedItems.size === billItems.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(billItems.map((i: DictType) => String(i.id))))
    }
  }

  // ── Bulk "Approve All Unchanged" ──
  const approveAllUnchanged = () => {
    const next = { ...itemStates }
    billItems.forEach((item: DictType) => {
      const { requested, contractQty, alreadyPaid } = getItemMetrics(item)
      const remaining = contractQty - alreadyPaid
      if (requested <= remaining) {
        next[item.id] = { ...next[item.id], approved: requested, declined: false }
      }
    })
    setItemStates(next)
    setSelectedItems(new Set(billItems.map((i: DictType) => String(i.id))))
  }

  // ── API call ──
  const approveBill = async (payload: DictType[]) => {
    const response = await axiosServices.post(
      FINANCIAL_SERVER_URL + BILL_APPROVE_ENDPOINT,
      {
        bill_id: bill?.id,
        items: payload,
        finish_approval: true,
      },
    )
    return response.data
  }

  const approvalMutation = useMutation({
    mutationKey: ["comparison-approve-bill", bill?.id],
    mutationFn: approveBill,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("bill items approved successfully"),
        variant: "success",
      })
      onOpenChange(false)
      revalidate()
    },
    onError: (error: any) => {
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "approve bill", translate),
        variant: "destructive",
      })
    },
  })

  // ── Submit handler ──
  const handleApproveSelected = () => {
    const payload = billItems
      .filter((item: DictType) => selectedItems.has(String(item.id)))
      .map((item: DictType) => ({
        bill_item_id: item.id,
        amount: itemStates[item.id]?.approved ?? item.amount,
        declined: itemStates[item.id]?.declined ?? false,
        message_text: itemStates[item.id]?.note ?? "",
      }))

    if (payload.length === 0) {
      toast({
        title: translate("Error"),
        description: translate("No items selected"),
        variant: "destructive",
      })
      return
    }

    approvalMutation.mutate(payload)
  }

  // ── Counts ──
  const overRequestCount = billItems.filter((item: DictType) => {
    const { isOverRequest } = getItemMetrics(item)
    return isOverRequest
  }).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-full max-h-[95vh] w-full max-w-[95vw] sm:max-w-[95vw] flex-col",
          isHebrew ? "rtl" : "ltr",
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(isHebrew ? "text-right" : "text-left")}>
            {translate("comparison_view")} — #{bill?.number}
          </DialogTitle>
          <DialogDescription className={cn(isHebrew ? "text-right" : "text-left")}>
            {billItems.length} {translate("Items")}
            {overRequestCount > 0 && (
              <Badge variant="destructive" className="mx-2">
                <AlertTriangle className="h-3 w-3 mx-1" />
                {overRequestCount} {translate("over_request")}
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* ── Bulk actions ── */}
        <div className="flex flex-wrap gap-2 py-2">
          <Button variant="outline" size="sm" onClick={approveAllUnchanged}>
            <CheckCircle2 className="h-4 w-4 mx-1" />
            {translate("approve_all_unchanged")}
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleApproveSelected}
            loading={approvalMutation.isPending}
          >
            <CheckCircle2 className="h-4 w-4 mx-1" />
            {translate("approve_selected")} ({selectedItems.size})
          </Button>
        </div>

        {/* ── Comparison table ── */}
        <ScrollArea className="h-full max-h-[60vh]">
          <Table className={cn("min-w-[900px]", isHebrew ? "rtl" : "ltr")}>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={selectedItems.size === billItems.length && billItems.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead className="w-[40px]">#</TableHead>
                <TableHead className={cn("min-w-[180px]", isHebrew ? "text-right" : "text-left")}>
                  {translate("Description")}
                </TableHead>
                <TableHead className="w-[60px] text-center">{translate("Unit")}</TableHead>
                <TableHead className="w-[90px] text-center">{translate("contract_quantity")}</TableHead>
                <TableHead className="w-[90px] text-center">{translate("already_paid")}</TableHead>
                <TableHead className="w-[90px] text-center">{translate("requested_now")}</TableHead>
                <TableHead className="w-[110px] text-center">{translate("approved_amount")}</TableHead>
                <TableHead className="w-[100px] text-center">{translate("remaining_after")}</TableHead>
                <TableHead className="w-[40px] text-center">{translate("decline_item")}</TableHead>
                <TableHead className="w-[40px] text-center">
                  <MessageSquare className="h-4 w-4 mx-auto" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billItems.map((item: DictType, index: number) => {
                const { contractQty, alreadyPaid, requested, approved, remainingAfter, isOverRequest, isApprovedOver } =
                  getItemMetrics(item)
                const state = itemStates[item.id]
                const isSelected = selectedItems.has(String(item.id))
                const isNoteExpanded = expandedNotes.has(String(item.id))

                return (
                  <React.Fragment key={item.id}>
                    <TableRow
                      className={cn(
                        "transition-colors",
                        state?.declined && "bg-red-50 opacity-60",
                        isOverRequest && !state?.declined && "bg-red-50/40",
                        !isOverRequest && !state?.declined && "bg-green-50/30",
                        isApprovedOver && !state?.declined && "border-l-2 border-l-orange-400",
                      )}
                    >
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelectItem(String(item.id))}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{index + 1}</TableCell>
                      <TableCell className={cn(isHebrew ? "text-right" : "text-left")}>
                        <p className="font-medium text-sm">{item?._boq_item?.description}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          {item?._boq_item?.number}
                        </p>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {item?._boq_item?.units}
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {contractQty.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {alreadyPaid.toLocaleString()}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-center font-mono text-sm font-semibold",
                          isOverRequest ? "text-red-600" : "text-green-600",
                        )}
                      >
                        {requested.toLocaleString()}
                        {isOverRequest && (
                          <Badge variant="destructive" className="mx-1 text-[10px] px-1 py-0">
                            {translate("over_request")}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          className={cn(
                            "w-[90px] mx-auto text-center text-sm",
                            isApprovedOver && "border-orange-400 ring-1 ring-orange-300",
                          )}
                          value={approved}
                          disabled={state?.declined}
                          onChange={(e) => updateApproved(String(item.id), Number(e.target.value))}
                        />
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-center font-mono text-sm font-semibold",
                          remainingAfter < 0 ? "text-red-600" : "text-green-600",
                        )}
                      >
                        {remainingAfter.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={state?.declined ?? false}
                          onCheckedChange={(checked) =>
                            updateDeclined(String(item.id), checked === true)
                          }
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => toggleNoteExpand(String(item.id))}
                        >
                          {isNoteExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>

                    {/* Expandable note row */}
                    {isNoteExpanded && (
                      <TableRow key={`${item.id}-note`} className="bg-muted/20">
                        <TableCell colSpan={11} className="py-2 px-4">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-medium text-muted-foreground pt-2 whitespace-nowrap">
                              {translate("item_notes")}:
                            </span>
                            <Textarea
                              className="min-h-[60px] text-sm flex-1"
                              placeholder={translate("item_notes")}
                              value={state?.note ?? ""}
                              onChange={(e) => updateNote(String(item.id), e.target.value)}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </ScrollArea>

        <DialogFooter className={cn("pt-2", isHebrew ? "sm:gap-2" : "sm:space-x-2")}>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {translate("Cancel")}
          </Button>
          <Button
            size="sm"
            onClick={handleApproveSelected}
            loading={approvalMutation.isPending}
          >
            <CheckCircle2 className="h-4 w-4 mx-1" />
            {translate("bulk_approve")} ({selectedItems.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
